import Card from "@/design-system/Card/Card";
import { EvolutionFooter } from "@/design-system/Evolution/EvolutionFooter";
import { Menu } from "@/design-system/Menu/Menu";
import {
  EvolutionUpgradePlayerCard,
  PlayerCardWithMetaRating,
} from "@/design-system/PlayerCard/PlayerCard";
import { trackViewContent } from "@/events/evolutionsMixpanel";
import { getEvoStateIcon } from "@/pages/EvolutionsHub/EvolutionsOverviewPage/EvolutionsOverviewPage";
import type { SavedEvolutionPlayer } from "@repo/shared/api";
import { evolutionFooterFrom } from "@repo/shared/evolutions";
import { formatDate, useTranslation } from "@repo/shared/i18n";
import { Trash2 } from "lucide-react";

type Props = {
  savedPlayer: SavedEvolutionPlayer;
  onRemove: () => void;
  onMarkCompleted: () => void;
  onMarkPlanned: () => void;
  onUnmarkPlanned: () => void;
  handleClickSavedEvo: (savedPlayerId: string) => void;
};

export const SavedEvoCard = ({
  savedPlayer,
  onRemove,
  onMarkCompleted,
  onMarkPlanned,
  onUnmarkPlanned,
  handleClickSavedEvo,
}: Props) => {
  const { t } = useTranslation(["evolutions", "common"]);
  const { basePlayer, player, createdAt } = savedPlayer;
  const isPlanned = savedPlayer.state === "PLANNED";
  const createdDate = formatDate(createdAt * 1000, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const footer = evolutionFooterFrom(player.evolutionData);

  return (
    <Card
      className="relative flex cursor-pointer flex-col gap-y-2 p-3"
      hoverable
    >
      <div
        role="link"
        tabIndex={0}
        className="flex flex-col gap-y-2 outline-none"
        onClick={() => {
          trackViewContent({
            subsection: "Planned Evolutions",
            content_type: "evo_player",
            action_type: "core",
            new_content: false,
          });
          handleClickSavedEvo(savedPlayer.id);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleClickSavedEvo(savedPlayer.id);
        }}
      >
        <div className="flex h-8 items-center gap-x-2 pr-9">
          <span className="text-xs font-medium text-foreground-muted">
            {createdDate}
          </span>
          {isPlanned && (
            <span className="flex h-6 items-center gap-x-1 rounded-full bg-control px-2.5 text-xs font-medium text-foreground-body">
              <span className="size-3.5 shrink-0">
                {getEvoStateIcon("planned", 14)}
              </span>
              {t("states.labels.planned")}
            </span>
          )}
        </div>
        {basePlayer ? (
          <EvolutionUpgradePlayerCard
            hideBackground
            hidePrice
            showBasePlayerMetaRating
            basePlayer={basePlayer}
            evolutionPlayer={player}
          />
        ) : (
          <PlayerCardWithMetaRating
            hideBackground
            hideLabel
            hidePrice
            player={player}
          />
        )}
        {footer && (
          <EvolutionFooter
            {...footer}
            assetsBaseUrl={import.meta.env.VITE_APP_ASSETS_BASE_URL}
          />
        )}
      </div>
      {/* Outside the link: picking an action must not open the player. */}
      <div className="absolute top-3 right-3">
        <Menu
          align="end"
          items={[
            {
              value: "remove",
              label: t("actions.removeSaved"),
              icon: <Trash2 className="size-4" />,
              onSelect: onRemove,
            },
            {
              value: "planned",
              label: t(
                isPlanned ? "actions.unmarkPlanned" : "actions.markPlanned",
              ),
              icon: getEvoStateIcon("planned", 16),
              onSelect: isPlanned ? onUnmarkPlanned : onMarkPlanned,
            },
            {
              value: "completed",
              label: t("actions.markCompleted"),
              icon: getEvoStateIcon("completed", 16),
              onSelect: onMarkCompleted,
            },
          ]}
        />
      </div>
    </Card>
  );
};
